import { useEffect, useCallback, useRef } from 'react';
import { PLANS, PLAN_STATUS } from '../../utils/constants';
import { ERROR_MESSAGES, ALERT_MESSAGES } from './flowPlannerConstants';
import { fetchPlan } from './flowPlannerApi';
import { initializeModel } from './FlowPlannerModel';
import {
  publishFlowPlannerCountMetric,
  publishFlowPlannerTimeMetric,
  FLOW_PLANNER_METRICS as M
} from '../../utils/metricUtil';

const POLL_INTERVAL_MS = 5000;
const POLL_INTERVAL_FAST_MS = 500;
const POLL_MAX_ATTEMPTS = 60;
const POLL_MAX_ATTEMPTS_FAST = 600;

/** Polls a plan by ID until it reaches a terminal status, then updates state. */
export default function usePlanPolling(responseDataForGivenPlan, setPlanStatus, setModel, setError) {
  const pollTimerRef = useRef(null);
  const remainingRef = useRef(POLL_MAX_ATTEMPTS);
  const intervalRef = useRef(POLL_INTERVAL_MS);
  const pollRef = useRef(null);
  const pollStartTimeRef = useRef(null);
  const callerStartTimeRef = useRef(null);
  const e2eMetricRef = useRef(M.CREATE_PLAN_E2E);

  useEffect(
    () => () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    },
    []
  );

  const stopPolling = useCallback(() => {
    clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  }, []);

  const poll = useCallback(
    async (pollingPlanId) => {
      remainingRef.current--;
      try {
        const plan = await fetchPlan(pollingPlanId);
        if (
          [PLAN_STATUS.PENDING_APPROVAL, PLAN_STATUS.PENDING_USER_INPUT, PLAN_STATUS.RECEIVED_USER_INPUT].includes(
            plan.status
          )
        ) {
          stopPolling();
          if (callerStartTimeRef.current) {
            publishFlowPlannerTimeMetric(e2eMetricRef.current, callerStartTimeRef.current);
          }
          if (pollStartTimeRef.current) {
            publishFlowPlannerTimeMetric(M.POLL_DURATION, pollStartTimeRef.current);
          }
          responseDataForGivenPlan[PLANS.FLOW_PLANNER] = JSON.stringify(plan);
          setPlanStatus(plan.status);
          try {
            setModel(initializeModel(JSON.stringify(plan)));
          } catch (e) {
            console.error('Flow Planner — Failed to reinitialize model:', e);
          }
        } else if (plan.status === PLAN_STATUS.FAILED) {
          stopPolling();
          publishFlowPlannerCountMetric(M.PLAN_FAILED);
          setPlanStatus(PLAN_STATUS.FAILED);
          setError(plan.errorMessage || ALERT_MESSAGES.CREATION_FAILED);
        } else if (remainingRef.current <= 0) {
          stopPolling();
          publishFlowPlannerCountMetric(M.POLL_TIMEOUT);
          setPlanStatus(PLAN_STATUS.FAILED);
          setError(ERROR_MESSAGES.POLL_TIMEOUT);
        } else {
          pollTimerRef.current = setTimeout(() => pollRef.current(pollingPlanId), intervalRef.current);
        }
      } catch (e) {
        stopPolling();
        publishFlowPlannerCountMetric(M.POLL_ERROR);
        setPlanStatus(PLAN_STATUS.FAILED);
        setError(e.message || ERROR_MESSAGES.POLL_ERROR);
      }
    },
    [responseDataForGivenPlan, stopPolling, setPlanStatus, setModel, setError]
  );

  pollRef.current = poll;

  return useCallback(
    (pollingPlanId, { fast, startTime, e2eMetric } = {}) => {
      stopPolling();
      remainingRef.current = fast ? POLL_MAX_ATTEMPTS_FAST : POLL_MAX_ATTEMPTS;
      intervalRef.current = fast ? POLL_INTERVAL_FAST_MS : POLL_INTERVAL_MS;
      pollStartTimeRef.current = Date.now();
      callerStartTimeRef.current = startTime || null;
      e2eMetricRef.current = e2eMetric || M.CREATE_PLAN_E2E;
      pollRef.current(pollingPlanId);
    },
    [stopPolling]
  );
}
